import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

export type Column<T> = {
  key: string
  header: string
  /** cell content; default — the row's `key` field as text */
  render?: (r: T) => ReactNode
  /** data cell (ids, keys, numbers): JetBrains Mono */
  mono?: boolean
  /** CSS width of the column (table-layout: fixed); columns without it share the rest */
  width?: string
  /** full value for the truncated cell's tooltip; default — the raw text value */
  title?: (r: T) => string | undefined
  align?: 'left' | 'right'
  /** header becomes a sort toggle (with `sort` + `onSort` on the table) */
  sortable?: boolean
}

export type Sort = { key: string; dir: 'asc' | 'desc' }

type Props<T> = {
  rows: T[]
  columns: Column<T>[]
  rowId: (r: T) => string
  selectedId?: string | null
  onSelect?: (id: string) => void
  /** shown instead of the body when there are no rows */
  empty?: ReactNode
  /** accessible name of the grid */
  label?: string
  /** current sort (rows are expected sorted already); headers of sortable columns toggle it */
  sort?: Sort | null
  onSort?: (key: string) => void
}

const rawText = (r: unknown, key: string): string | undefined => {
  const v = (r as Record<string, unknown>)[key]
  return typeof v === 'string' || typeof v === 'number' ? String(v) : undefined
}

/**
 * Dense data table: sticky header, ~30 px rows, cells truncate with a tooltip,
 * ↑/↓/Home/End move between rows, Enter selects (opens details), aria-selected.
 * Scrolls inside its own container — the page never scrolls sideways.
 */
export function DataTable<T>({
  rows, columns, rowId, selectedId = null, onSelect, empty, label, sort = null, onSort,
}: Props<T>) {
  const ids = rows.map(rowId)
  const selectedIndex = selectedId === null ? -1 : ids.indexOf(selectedId)
  const [focusIndex, setFocusIndex] = useState(Math.max(0, selectedIndex))
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([])

  // keep the roving tab stop on an existing row
  useEffect(() => {
    if (focusIndex > rows.length - 1) setFocusIndex(Math.max(0, rows.length - 1))
  }, [rows.length, focusIndex])
  useEffect(() => {
    if (selectedIndex < 0) return
    setFocusIndex(selectedIndex)
    // a ?sel= link may point far down the list
    rowRefs.current[selectedIndex]?.scrollIntoView?.({ block: 'nearest' })
  }, [selectedIndex])

  const moveTo = (i: number) => {
    const j = Math.min(Math.max(i, 0), rows.length - 1)
    setFocusIndex(j)
    rowRefs.current[j]?.focus()
  }

  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, i: number) => {
    // keys typed into a link/checkbox inside the row belong to that control
    if (e.target !== e.currentTarget) return
    if (e.key === 'ArrowDown') moveTo(i + 1)
    else if (e.key === 'ArrowUp') moveTo(i - 1)
    else if (e.key === 'Home') moveTo(0)
    else if (e.key === 'End') moveTo(rows.length - 1)
    else if (e.key === 'Enter' || e.key === ' ') onSelect?.(ids[i])
    else return
    e.preventDefault()
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <table
        role="grid"
        aria-label={label}
        className="w-full table-fixed border-separate border-spacing-0 text-[12.5px]"
      >
        <colgroup>
          {columns.map((c) => <col key={c.key} style={c.width ? { width: c.width } : undefined} />)}
        </colgroup>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                className={[
                  'sticky top-0 z-[1] truncate border-b border-line bg-background px-2.5 py-[7px] text-[11px] font-medium uppercase tracking-[0.05em] text-text-3 first:pl-4 last:pr-4',
                  c.align === 'right' ? 'text-right' : 'text-left',
                ].join(' ')}
              >
                {c.sortable && onSort
                  ? (
                    <button
                      type="button"
                      onClick={() => onSort(c.key)}
                      className="inline-flex items-center gap-1 uppercase tracking-[0.05em] transition-colors hover:text-foreground"
                    >
                      {c.header}
                      <span aria-hidden className={sort?.key === c.key ? 'text-foreground' : ''}>
                        {sort?.key === c.key ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  )
                  : c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const id = ids[i]
            const selected = i === selectedIndex
            return (
              <tr
                key={id}
                ref={(el) => { rowRefs.current[i] = el }}
                data-row-id={id}
                aria-selected={selected}
                tabIndex={i === focusIndex ? 0 : -1}
                onClick={() => { setFocusIndex(i); onSelect?.(id) }}
                onKeyDown={(e) => onRowKey(e, i)}
                className={[
                  'cursor-default outline-none transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary',
                  selected ? 'bg-surface-selected' : 'hover:bg-line-row',
                ].join(' ')}
              >
                {columns.map((c) => {
                  const title = c.title ? c.title(r) : rawText(r, c.key)
                  return (
                    <td
                      key={c.key}
                      title={title}
                      className={[
                        'h-[30px] truncate border-b border-line-row px-2.5 py-[5px] first:pl-4 last:pr-4',
                        c.mono ? 'font-mono text-[12px]' : '',
                        c.align === 'right' ? 'text-right' : '',
                      ].join(' ')}
                    >
                      {c.render ? c.render(r) : rawText(r, c.key)}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      {rows.length === 0 && empty && <div className="px-4 py-6 text-[13px] text-text-2">{empty}</div>}
    </div>
  )
}
